/**
 * @fileoverview Service class for managing spinner data in Firestore.
 * 
 * This service abstracts the logic for creating, retrieving, updating, and deleting
 * spinner configurations. It handles different expiration rules for anonymous
 * and authenticated users and manages the lifecycle of shared spinners.
 */

import { db } from '../firebase/config';
import { collection, addDoc, getDoc, doc, updateDoc, query, where, getDocs, Timestamp, deleteDoc, orderBy } from 'firebase/firestore';
import { auth } from '../firebase/config';

/**
 * Interface representing the comprehensive data structure for a spinner,
 * including its content, settings, and metadata.
 */
export interface SpinnerData {
  id: string;
  participants: string[];
  style: string;
  language: string;
  theme: string;
  sound: boolean;
  spinTime: number;
  createdAt: Date;
  name: string;
  userId: string;
  lastAccessed: Date;
  expiresAt: Date;
  isActive: boolean;
  isShared?: boolean;
}

export interface UserResult {
  id: string;
  winner: string;
  type: string;
  timestamp: number;
  spinnerName: string;
  gameType?: string;
  gameResultCandidate?: string;
}

/**
 * Manages all CRUD (Create, Read, Update, Delete) operations for spinners in Firestore.
 */
class SpinnerService {
  private readonly collectionName = 'spinners';
  private readonly SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  private readonly ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

  /**
   * Generates a unique name for a spinner by appending numbers if the name already exists.
   * @param {string} baseName - The original name to check.
   * @param {string} userId - The user ID to check against.
   * @returns {Promise<string>} A unique name for the spinner.
   */
  private async generateUniqueName(baseName: string, userId: string): Promise<string> {
    const userSpinners = await this.getUserSpinners(userId);
    const existingNames = userSpinners.map(spinner => spinner.name);
    
    if (!existingNames.includes(baseName)) {
      return baseName;
    }
    
    let counter = 1;
    let newName = `${baseName}-${counter}`;
    
    while (existingNames.includes(newName)) {
      counter++;
      newName = `${baseName}-${counter}`;
    }
    
    return newName;
  }

  /**
   * Creates a new spinner document in Firestore for sharing.
   * Sets a 1-year expiration for authenticated users, and 7-day for anonymous.
   * Automatically handles duplicate names by appending numbers (-1, -2, etc.).
   * @param {Omit<SpinnerData, 'id'>} data - The spinner settings and content to be saved.
   * @returns {Promise<string>} The ID of the newly created spinner document.
   */
  async shareSpinner(data: Omit<SpinnerData, 'id'>): Promise<string> {
    const user = auth.currentUser;
    const now = new Date();
    const expiresAt = user ? new Date(now.getTime() + this.ONE_YEAR_MS) : new Date(now.getTime() + this.SEVEN_DAYS_MS);

    // Generate unique name if user is authenticated
    let uniqueName = data.name;
    if (user) {
      uniqueName = await this.generateUniqueName(data.name, user.uid);
    }

    const spinnerData: any = {
      ...data,
      name: uniqueName,
      createdAt: Timestamp.fromDate(now),
      lastAccessed: Timestamp.fromDate(now),
      expiresAt: Timestamp.fromDate(expiresAt),
      isActive: false
    };

    if (user) {
      spinnerData.userId = user.uid;
    }

    const docRef = await addDoc(collection(db, this.collectionName), spinnerData);
    return docRef.id;
  }

  /**
   * Creates a share link for a spinner (original spinner, not a copy).
   * This method creates the original spinner that will be shared.
   * @param {Omit<SpinnerData, 'id'>} data - The spinner settings and content to be saved.
   * @returns {Promise<string>} The ID of the newly created spinner document.
   */
  async createShareLink(data: Omit<SpinnerData, 'id'>): Promise<string> {
    const user = auth.currentUser;
    const now = new Date();
    const expiresAt = user ? new Date(now.getTime() + this.ONE_YEAR_MS) : new Date(now.getTime() + this.SEVEN_DAYS_MS);

    // Generate unique name if user is authenticated
    let uniqueName = data.name;
    if (user) {
      uniqueName = await this.generateUniqueName(data.name, user.uid);
    }

    const spinnerData: any = {
      ...data,
      name: uniqueName,
      createdAt: Timestamp.fromDate(now),
      lastAccessed: Timestamp.fromDate(now),
      expiresAt: Timestamp.fromDate(expiresAt),
      isActive: true, // Mark as active since this is the original
      isShared: true  // Mark as shared for tracking
    };

    if (user) {
      spinnerData.userId = user.uid;
    }

    const docRef = await addDoc(collection(db, this.collectionName), spinnerData);
    return docRef.id;
  }

  /**
   * Retrieves a spinner by its ID, handling expiration and access updates.
   * If the spinner is expired, it is deleted. Otherwise, its 'lastAccessed'
   * timestamp and expiration date are updated.
   * @param {string} id - The unique ID of the spinner to retrieve.
   * @returns {Promise<SpinnerData | null>} The spinner data, or null if not found or expired.
   */
  async getSpinner(id: string): Promise<SpinnerData | null> {
    const docRef = doc(db, this.collectionName, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    const now = new Date();
    const expiresAt = data.expiresAt?.toDate();

    // Check if spinner has expired
    if (expiresAt && expiresAt < now) {
      await deleteDoc(docRef);
      return null;
    }

    // Update last accessed time and mark as active
    await updateDoc(docRef, {
      lastAccessed: Timestamp.fromDate(now),
      isActive: true,
      expiresAt: Timestamp.fromDate(new Date(now.getTime() + this.ONE_YEAR_MS))
    });

    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt.toDate(),
      lastAccessed: data.lastAccessed?.toDate() || now,
      expiresAt: data.expiresAt?.toDate() || new Date(now.getTime() + this.ONE_YEAR_MS),
      name: data.name || '',
      userId: data.userId || '',
      isActive: true,
      isShared: data.isShared || false
    } as SpinnerData;
  }

  /**
   * Updates the name of a specific spinner document.
   * @param {string} id - The ID of the spinner to update.
   * @param {string} name - The new name for the spinner.
   */
  async updateSpinnerName(id: string, name: string): Promise<void> {
    const docRef = doc(db, this.collectionName, id);
    await updateDoc(docRef, { name });
  }

  /**
   * Retrieves all spinners belonging to a specific user.
   * This method also performs cleanup, deleting any spinners that have expired.
   * @param {string} userId - The UID of the user whose spinners to retrieve.
   * @returns {Promise<SpinnerData[]>} A list of the user's active spinners.
   */
  async getUserSpinners(userId: string): Promise<SpinnerData[]> {
    const q = query(
      collection(db, this.collectionName),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const now = new Date();
    const spinners: SpinnerData[] = [];

    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      const expiresAt = data.expiresAt?.toDate();
      const lastAccessed = data.lastAccessed?.toDate();

      // Delete expired spinners
      if (expiresAt && expiresAt < now) {
        await deleteDoc(doc.ref);
        continue;
      }

      // Update expiration for inactive spinners
      if (!data.isActive && lastAccessed) {
        const timeSinceLastAccess = now.getTime() - lastAccessed.getTime();
        if (timeSinceLastAccess >= this.SEVEN_DAYS_MS) {
          await deleteDoc(doc.ref);
          continue;
        }
      }

      spinners.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt.toDate(),
        lastAccessed: lastAccessed || now,
        expiresAt: expiresAt || new Date(now.getTime() + this.ONE_YEAR_MS),
        name: data.name || '',
        userId: data.userId || '',
        isActive: data.isActive || false
      } as SpinnerData);
    }

    return spinners;
  }

  /**
   * Deletes a spinner document from Firestore.
   * @param {string} id - The ID of the spinner to delete.
   */
  async deleteSpinner(id: string): Promise<void> {
    const docRef = doc(db, this.collectionName, id);
    await deleteDoc(docRef);
  }
}

/**
 * Singleton instance of the SpinnerService for use throughout the application.
 */
export const spinnerService = new SpinnerService();

export const saveUserResult = async (userId: string, result: UserResult) => {
  const resultsRef = collection(db, 'userResults', userId, 'results');
  
  // Only include defined values to avoid Firestore errors
  const dataToSave: any = {
    id: result.id,
    winner: result.winner,
    type: result.type,
    timestamp: result.timestamp || Date.now(),
    spinnerName: result.spinnerName,
  };
  
  // Only add optional fields if they are defined
  if (result.gameType !== undefined) {
    dataToSave.gameType = result.gameType;
  }
  
  if (result.gameResultCandidate !== undefined) {
    dataToSave.gameResultCandidate = result.gameResultCandidate;
  }
  
  await addDoc(resultsRef, dataToSave);
};

export const getUserResults = async (userId: string): Promise<UserResult[]> => {
  const resultsRef = collection(db, 'userResults', userId, 'results');
  const q = query(resultsRef, orderBy('timestamp', 'desc'));
  
  const snapshot = await getDocs(q);
  const results = snapshot.docs.map(doc => {
    const data = doc.data();
    // Convert Firestore Timestamp to number if needed
    let timestamp = data.timestamp;
    if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
      timestamp = timestamp.toDate().getTime();
    }
    return { id: doc.id, ...data, timestamp } as UserResult;
  });

 
  
  return results;
};

export const clearUserResults = async (userId: string): Promise<void> => {
  const resultsRef = collection(db, 'userResults', userId, 'results');
  const q = query(resultsRef);
  const snapshot = await getDocs(q);
  
  // Delete all results
  const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
  await Promise.all(deletePromises);
}; 